import type { ServiceArea } from "@tdelivr/api-client/generated/geo/service-area.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

/**
 * What the zones board does to the areas GEO-06 returns, away from any rendering.
 *
 * The same split as the dispatch console's `offer-monitor.ts`: searching, ordering, paging and
 * deciding what an area's state actually is are all stateable and testable without a browser.
 *
 * **The finding this screen exists to record is that `lifecycle` is not coverage, and that an
 * `active` area is not necessarily serving anybody.** Both halves are below, and R-13 in
 * `docs/design/DESIGN_RECONCILIATION.md` is the measurement.
 */

/** GEO-06's published lifecycle enum, as one name. */
export type AreaLifecycle = ServiceArea["lifecycle"];

/** Every lifecycle the listing's own filter accepts, in the contract's order. */
export const FILTERABLE_LIFECYCLES: readonly AreaLifecycle[] = ["draft", "active", "inactive"];

/**
 * How many areas to take in one request.
 *
 * 200 is GEO-06's own cap, stated in the parameter's own description: "defaulting to 50 and capped
 * at 200. A limit above the cap is clamped rather than refused." The design draws 34 zones, so one
 * request is the whole board for a tenant of that size — and when it is not, the board says so.
 */
export const PER_FETCH = "200";

/**
 * Whether this area is actually serving at a given instant.
 *
 * **GEO-06's own availability query is the authority, and `lifecycle` is only one of its three
 * clauses.** `POST /v1/service-areas/availability` — the operation that decides whether a customer
 * standing somewhere can book — selects on
 *
 * ```sql
 * lifecycle='active' AND effective_from<=$at AND (effective_until IS NULL OR effective_until>$at)
 * ```
 *
 * So an area stored `active` with an `effectiveFrom` in the future, or an `effectiveUntil` in the
 * past, is not in the candidate set: the platform will not offer it, and a customer there gets
 * nothing. Its row still reads `active`.
 *
 * That is why this screen does not draw the stored lifecycle on its own. A board showing `active`
 * for such a row would tell an administrator a zone is open for business while the platform has
 * already closed it — and the two fields that correct it, `effectiveFrom` and `effectiveUntil`, are
 * published on every row, so the screen can simply be right. The same shape as the offer monitor's
 * `lapsed()`, on a different service.
 *
 * Only `active` areas are tested against the window. A draft area's dates are a plan and an
 * inactive one's are history; neither is a claim about now.
 */
export function servingNow(area: ServiceArea, now: number): boolean {
  if (area.lifecycle !== "active") return false;
  const from = Date.parse(area.effectiveFrom);
  if (Number.isFinite(from) && from > now) return false;
  if (area.effectiveUntil !== undefined) {
    const until = Date.parse(area.effectiveUntil);
    if (Number.isFinite(until) && until <= now) return false;
  }
  return true;
}

/** Which side of its window an `active` area falls, when it is not inside it. */
export type WindowPosition = "inside" | "not_yet" | "ended";

export function windowPosition(area: ServiceArea, now: number): WindowPosition {
  const from = Date.parse(area.effectiveFrom);
  if (Number.isFinite(from) && from > now) return "not_yet";
  if (area.effectiveUntil !== undefined) {
    const until = Date.parse(area.effectiveUntil);
    if (Number.isFinite(until) && until <= now) return "ended";
  }
  return "inside";
}

/**
 * The state class and the word.
 *
 * **The design's three words are not this service's, and substituting them would be a false
 * statement rather than a translation.** `17 WP16 Tenant Admin.dc.html` draws `served`, `partial`
 * and `review request` over a column headed `Coverage`, and its purpose line is "Service areas,
 * partial coverage and the reason shown to customers outside them". GEO-06 publishes
 * `lifecycle: draft | active | inactive`, which answers a different question — whether the *record*
 * is in force, not how much of the service the area gets. Specifically:
 *
 *  - `served` would claim every service is available there. The row says which products are
 *    configured (`productRefs`) and nothing about hours, so "all services" is not knowable.
 *  - **`partial` has no field at all**, and it is the page's stated reason for existing. The design's
 *    `rides only after 22:00` and `Boda only` are an hours restriction and a product restriction;
 *    the second is `productRefs` and the first is nowhere on the row, in the boundary, or in any
 *    GEO-06 operation.
 *  - `review request` is a customer asking for an area that does not exist yet. Nothing on the
 *    platform records such a request.
 *
 * Collapsing three coverage words onto two lifecycle values would put a zone with restricted hours
 * under `served`, which is the one error on this page that reaches a customer: an administrator who
 * believes Karen is fully served stops looking for the reason rides are refused there at nine in the
 * evening. So the words are the contract's own, and the gap is recorded.
 *
 * The classes are the design's, read off its own badge classes for the states that do correspond:
 * `draft` is `idle` — which is the state-class table's own example for the word — `active` inside its
 * window is `done`, and `inactive` is `closed`. An `active` area outside its window is `caution`,
 * the class whose label is "Qualified": it is in force as a record and not in force as a service,
 * which is exactly an outcome with a condition attached.
 */
export function stateOf(
  area: ServiceArea,
  now: number,
): { classKey: StateClassKey; label: string } {
  if (area.lifecycle === "draft") return { classKey: "idle", label: "draft" };
  if (area.lifecycle === "inactive") return { classKey: "closed", label: "inactive" };
  const position = windowPosition(area, now);
  if (position === "not_yet") return { classKey: "caution", label: "not yet effective" };
  if (position === "ended") return { classKey: "caution", label: "no longer effective" };
  return { classKey: "done", label: "active" };
}

/**
 * The design's `Coverage` cell, from the one coverage fact the row publishes.
 *
 * `productRefs` is what can be booked in this area, and the design's own `Boda only` is the shape
 * for a single-product area — so a one-product area keeps that grammar and a multi-product one lists
 * them. The references are published as opaque strings matching `^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$`
 * and nothing on the platform turns a product reference into a product's name, so they are shown as
 * published rather than prettified.
 *
 * The read schema puts no minimum on the array where the write schema requires at least one, so an
 * empty list is reachable on the wire and is said rather than drawn as an empty cell.
 */
export function coverageText(area: ServiceArea): string {
  if (area.productRefs.length === 0) return "no services configured";
  if (area.productRefs.length === 1) return `${area.productRefs[0]!} only`;
  return area.productRefs.join(" · ");
}

/**
 * What an `active` area's badge needs to say beyond its word, or nothing.
 *
 * On the badge's `title` rather than in a column of its own, because a column the design does not
 * draw is design content invented here — the same call the offer monitor made for its lapse reason.
 * An administrator who wonders why a zone they switched on reads "not yet effective" gets the answer
 * where they are already pointing.
 */
export function stateReason(area: ServiceArea, now: number): string | undefined {
  if (area.lifecycle !== "active") return undefined;
  const position = windowPosition(area, now);
  if (position === "not_yet") {
    return `Stored active, and effective from ${area.effectiveFrom}. GEO-06's availability evaluation requires the instant to be inside the effective window, so nothing can be booked here yet.`;
  }
  if (position === "ended") {
    return `Stored active, and effective until ${area.effectiveUntil ?? "an instant now past"}. GEO-06's availability evaluation requires the instant to be inside the effective window, so nothing can be booked here any more.`;
  }
  return undefined;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type ZoneOrder = "default" | "state" | "az" | "za";

export interface ZoneQuery {
  readonly search: string;
  readonly order: ZoneOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
  /** Now, for the effective-window rule, so a search for "no longer effective" finds its row. */
  readonly now: number;
}

export interface ZonePage {
  readonly rows: readonly ServiceArea[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly ServiceArea[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(area: ServiceArea, now: number): string {
  return [
    area.name,
    area.marketRef,
    area.productRefs.join(" "),
    coverageText(area),
    area.lifecycle,
    stateOf(area, now).label,
    area.id,
    area.boundary.kind === "geofence" ? `geofence ${area.boundary.geofenceId}` : "geometry",
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order GEO-06 answered in — `ORDER BY priority DESC, area_id DESC`, strongest
 * precedence first — and the screen must not quietly replace it. Precedence is what decides which
 * area's rules apply where two overlap, so a board re-sorted alphabetically buries the row that
 * wins.
 *
 * There is no lifecycle filter here: GEO-06's listing takes one as a query parameter, so choosing a
 * lifecycle refetches rather than hiding rows. That is the opposite of the audit board, and the
 * difference is the contract's rather than a preference — this listing publishes a filter and that
 * one does not.
 */
export function zonePage(rows: readonly ServiceArea[], query: ZoneQuery): ZonePage {
  const needle = query.search.trim().toLowerCase();
  const matched =
    needle.length === 0
      ? [...rows]
      : rows.filter((area) => searchableText(area, query.now).includes(needle));

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left, query.now).label.localeCompare(stateOf(right, query.now).label);
          }
          // "Name" is the first column, as the design's A–Z is: the zone's name.
          const compared = left.name.localeCompare(right.name);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The one sentence this board owes an administrator about what it fetched, or nothing.
 *
 * The design gives the grid no such line; the dispatch console's boards added one for the reason
 * R-08 defends. Here it carries one fact and only when it is true: GEO-06 pages on a composite
 * `(priority, area_id)` cursor, so following it is safe — unlike the audit board's — but this board
 * takes one page of 200 rather than walking the whole list, and a tenant with more areas than that
 * is looking at the strongest 200 precedences rather than all of them.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param truncated whether GEO-06 answered a `nextAfter`, meaning this is the head of the list.
 * @param lifecycle the lifecycle the administrator chose, or `undefined` for all three.
 */
export function boundedNote(
  fetched: number | undefined,
  truncated: boolean,
  lifecycle: AreaLifecycle | undefined,
  perFetch: number,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (truncated) {
    const scope = lifecycle === undefined ? "zones" : `${lifecycle} zones`;
    return `More than ${perFetch} ${scope} match. This board shows the ${perFetch} with the strongest precedence, so the count is a floor rather than a total.`;
  }
  return undefined;
}
